import { useMemo } from "react";
import { Link } from "react-router-dom";
import { useUser } from "@clerk/clerk-react";
import {
  Activity,
  ArrowRight,
  BookOpen,
  CheckCircle2,
  Cpu,
  FlaskConical,
  GitBranch,
  TrendingUp,
} from "lucide-react";
import { PageHeader } from "@/components/layout/page-header";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { LoadingState, ErrorState } from "@/components/ui/empty";
import { StatusBadge, EntryTypeBadge } from "@/components/status-badges";
import { useApi } from "@/lib/api";
import { formatFitness, formatRelative, shortId } from "@/lib/format";
import type {
  CampaignsResponse,
  EnginesResponse,
  ExperimentsResponse,
  JournalResponse,
  RungsResponse,
} from "@/lib/types";

export function DashboardPage() {
  const { user } = useUser();
  const greeting = user?.firstName
    ? `Welcome back, ${user.firstName}`
    : "Welcome back";

  const campaigns = useApi<CampaignsResponse>("/api/campaigns");
  const experiments = useApi<ExperimentsResponse>("/api/experiments");
  const engines = useApi<EnginesResponse>("/api/engines");
  const journal = useApi<JournalResponse>("/api/journal");
  const rungs = useApi<RungsResponse>("/api/rungs");

  const loading =
    campaigns.loading ||
    experiments.loading ||
    engines.loading ||
    journal.loading ||
    rungs.loading;
  const firstError =
    campaigns.error ||
    experiments.error ||
    engines.error ||
    journal.error ||
    rungs.error;

  const derived = useMemo(() => {
    const exps = experiments.data?.experiments ?? [];
    const runs = exps.filter((e) => e.status === "running").length;
    const done = exps.filter((e) => e.status === "completed").length;
    const failed = exps.filter((e) => e.status === "failed").length;
    const openCampaigns = (campaigns.data?.campaigns ?? []).filter(
      (c) => c.status === "open",
    );
    const activeCampaign =
      openCampaigns.find(
        (c) => c.id !== "cmp_bc205979-c0c0-4327-9cb8-763ec4694e9b",
      ) ?? openCampaigns[0];
    const rungList = rungs.data?.rungs ?? [];

    // Best fitness per rung (ceiling) across all experiments
    const bestByRung = new Map<
      string,
      { fitness: number; expId: string; engine: string }
    >();
    for (const e of exps) {
      if (e.final_fitness === null || e.final_fitness === undefined) continue;
      const prev = bestByRung.get(e.rung_id);
      if (!prev || e.final_fitness > prev.fitness) {
        bestByRung.set(e.rung_id, {
          fitness: e.final_fitness,
          expId: e.id,
          engine: e.engine_version,
        });
      }
    }
    const passed = rungList.filter((r) => {
      const best = bestByRung.get(r.id);
      return best && best.fitness >= r.fitness_threshold;
    }).length;

    return {
      runningCount: runs,
      completedCount: done,
      failedCount: failed,
      openCampaignCount: openCampaigns.length,
      activeCampaign,
      rungsPassed: passed,
      rungsTotal: rungList.length,
      bestByRung,
      rungList,
    };
  }, [experiments.data, campaigns.data, rungs.data]);

  if (loading && !experiments.data) {
    return (
      <>
        <PageHeader title={greeting} description="Loading lab state…" />
        <LoadingState />
      </>
    );
  }

  if (firstError && !experiments.data) {
    return (
      <>
        <PageHeader title={greeting} />
        <ErrorState error={firstError} />
      </>
    );
  }

  const latestEntry = journal.data?.entries[0];
  const recentExperiments = (experiments.data?.experiments ?? []).slice(0, 6);
  const canonicalEngine = engines.data?.engines.find((e) => e.canonical === 1);
  const latestEngine = engines.data?.engines[0];
  const engineToShow = canonicalEngine ?? latestEngine;

  return (
    <>
      <PageHeader
        title={greeting}
        description="Foundling Lab — LIMS for the Neuronite research loop."
      />

      {/* ── Stat strip ───────────────────────────────────────────────── */}
      <div className="mb-6 grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <StatCard
          icon={Activity}
          label="Running experiments"
          value={derived.runningCount.toString()}
          hint={`${derived.completedCount} completed · ${derived.failedCount} failed`}
          accent="amber"
        />
        <StatCard
          icon={FlaskConical}
          label="Open campaigns"
          value={derived.openCampaignCount.toString()}
          hint="research arcs in progress"
          accent="violet"
        />
        <StatCard
          icon={TrendingUp}
          label="Rungs passed"
          value={`${derived.rungsPassed} / ${derived.rungsTotal}`}
          hint="best-of across all engines"
          accent="emerald"
        />
        <StatCard
          icon={Cpu}
          label="Latest engine"
          value={engineToShow?.version ?? "—"}
          hint={
            engineToShow
              ? `${formatRelative(engineToShow.created_at)} · ${engineToShow.git_hash?.slice(0, 7) ?? "—"}`
              : "no engines yet"
          }
          accent="indigo"
        />
      </div>

      <div className="grid gap-6 lg:grid-cols-3">
        {/* ── Active campaign ──────────────────────────────────────────── */}
        <div className="lg:col-span-2 space-y-6">
          {derived.activeCampaign && (
            <Card className="relative overflow-hidden border-violet-500/20">
              <div className="absolute inset-0 bg-gradient-to-br from-violet-500/5 via-transparent to-transparent" />
              <CardHeader className="relative">
                <div className="flex items-center gap-2 text-xs text-violet-300">
                  <FlaskConical className="h-3.5 w-3.5" />
                  <span className="uppercase tracking-wider">
                    Active campaign
                  </span>
                </div>
                <CardTitle className="mt-1 text-xl">
                  {derived.activeCampaign.name}
                </CardTitle>
                {derived.activeCampaign.layer_focus !== null && (
                  <CardDescription>
                    Layer focus: L{derived.activeCampaign.layer_focus} ·
                    Started {formatRelative(derived.activeCampaign.started_at)}
                  </CardDescription>
                )}
              </CardHeader>
              <CardContent className="relative space-y-4">
                <blockquote className="border-l-2 border-violet-400/60 pl-4 text-sm italic text-muted-foreground">
                  {truncate(derived.activeCampaign.lead_question, 280)}
                </blockquote>
                <Link
                  to={`/campaigns/${derived.activeCampaign.id}`}
                  className="inline-flex items-center gap-1 text-xs text-violet-300 hover:text-violet-200"
                >
                  View campaign <ArrowRight className="h-3 w-3" />
                </Link>
              </CardContent>
            </Card>
          )}

          {/* ── Ceiling board (derived) ──────────────────────────────── */}
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-3">
              <div>
                <CardTitle className="text-base">Ceiling board</CardTitle>
                <CardDescription className="text-xs">
                  Best-of fitness per rung across all experiments
                </CardDescription>
              </div>
              <Link
                to="/ceiling"
                className="text-xs text-muted-foreground hover:text-foreground"
              >
                Full view <ArrowRight className="inline h-3 w-3" />
              </Link>
            </CardHeader>
            <CardContent className="space-y-3">
              {derived.rungList.map((r) => {
                const best = derived.bestByRung.get(r.id);
                const frac = best ? best.fitness / 1 : 0;
                const passed =
                  best && best.fitness >= r.fitness_threshold;
                return (
                  <div key={r.id} className="space-y-1">
                    <div className="flex items-center justify-between text-xs">
                      <div className="flex items-center gap-2">
                        <span className="font-mono font-medium">{r.id}</span>
                        <span className="text-muted-foreground">
                          {r.name}
                        </span>
                        {passed && (
                          <CheckCircle2 className="h-3 w-3 text-emerald-400" />
                        )}
                      </div>
                      <span className="font-mono tabular-nums text-muted-foreground">
                        {formatFitness(best?.fitness ?? null)} /{" "}
                        {r.fitness_threshold.toFixed(2)}
                      </span>
                    </div>
                    <div className="relative">
                      <Progress
                        value={frac}
                        barClassName={
                          passed
                            ? "bg-emerald-500"
                            : best
                              ? "bg-amber-400"
                              : "bg-muted-foreground/30"
                        }
                      />
                      <div
                        className="pointer-events-none absolute top-0 h-1.5 w-px bg-foreground/60"
                        style={{
                          left: `${r.fitness_threshold * 100}%`,
                        }}
                        title={`pass threshold ${r.fitness_threshold}`}
                      />
                    </div>
                  </div>
                );
              })}
            </CardContent>
          </Card>
        </div>

        {/* ── Right column ─────────────────────────────────────────── */}
        <div className="space-y-6">
          {/* Latest journal entry */}
          {latestEntry && (
            <Card>
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-base flex items-center gap-2">
                    <BookOpen className="h-4 w-4 text-muted-foreground" />
                    Latest finding
                  </CardTitle>
                  <Link
                    to="/journal"
                    className="text-xs text-muted-foreground hover:text-foreground"
                  >
                    All <ArrowRight className="inline h-3 w-3" />
                  </Link>
                </div>
              </CardHeader>
              <CardContent className="space-y-2">
                <EntryTypeBadge entryType={latestEntry.entry_type} />
                <Link
                  to={`/journal/${latestEntry.id}`}
                  className="block text-sm font-medium leading-snug hover:text-primary"
                >
                  {latestEntry.title}
                </Link>
                <div className="flex items-center gap-2 text-[10px] text-muted-foreground">
                  <span>{latestEntry.author}</span>
                  <span>·</span>
                  <span>{formatRelative(latestEntry.timestamp)}</span>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Recent experiments */}
          <Card>
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <CardTitle className="text-base flex items-center gap-2">
                  <GitBranch className="h-4 w-4 text-muted-foreground" />
                  Recent experiments
                </CardTitle>
                <Link
                  to="/experiments"
                  className="text-xs text-muted-foreground hover:text-foreground"
                >
                  All <ArrowRight className="inline h-3 w-3" />
                </Link>
              </div>
            </CardHeader>
            <CardContent className="space-y-2">
              {recentExperiments.map((e) => (
                <Link
                  key={e.id}
                  to={`/experiments/${e.id}`}
                  className="flex items-center justify-between gap-2 rounded-md px-2 py-1.5 text-xs hover:bg-accent"
                >
                  <div className="flex min-w-0 items-center gap-2">
                    <StatusBadge status={e.status} />
                    <span className="font-mono text-muted-foreground">
                      {e.rung_id}
                    </span>
                    <span className="truncate text-muted-foreground">
                      @ {e.engine_version}
                    </span>
                  </div>
                  <span className="shrink-0 font-mono tabular-nums text-muted-foreground">
                    {e.final_fitness !== null
                      ? formatFitness(e.final_fitness)
                      : shortId(e.id)}
                  </span>
                </Link>
              ))}
            </CardContent>
          </Card>
        </div>
      </div>
    </>
  );
}

function truncate(s: string, n: number): string {
  if (s.length <= n) return s;
  return s.slice(0, n).replace(/\s+\S*$/, "") + "…";
}

function StatCard({
  icon: Icon,
  label,
  value,
  hint,
  accent,
}: {
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  value: string;
  hint: string;
  accent: "amber" | "violet" | "emerald" | "indigo";
}) {
  const accentCls = {
    amber: "text-amber-400 bg-amber-500/10",
    violet: "text-violet-400 bg-violet-500/10",
    emerald: "text-emerald-400 bg-emerald-500/10",
    indigo: "text-indigo-400 bg-indigo-500/10",
  }[accent];

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <CardDescription className="text-xs uppercase tracking-wide">
          {label}
        </CardDescription>
        <div className={`rounded-md p-1.5 ${accentCls}`}>
          <Icon className="h-4 w-4" />
        </div>
      </CardHeader>
      <CardContent>
        <div className="text-2xl font-semibold tabular-nums">{value}</div>
        <p className="mt-1 text-xs text-muted-foreground">{hint}</p>
      </CardContent>
    </Card>
  );
}
